import * as React from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import MUITextField from "@mui/material/TextField";
import {
  FormControl,
  FormLabel,
  Box,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AxiosError } from "axios";
import Title from "@/components/molecules/Title";
import { ActionButtons } from "@/components/common/buttonGroup/ActionButtons";
import { ConfirmDialog } from "@/components/molecules/ConfirmDialog";
import {
  CarUnderuseEventsConstants,
  CarUnderuseEventsEditState,
  CarUnderuseEventsEditError,
  INITIAL_EDIT_STATE,
  UNDERUSE_REASONS,
  resolveEditRequest,
  getEditWarnMessage,
  formatYearMonth,
  UnderuseReasonCodes,
  getUnderuseReasonLabel,
  resolveRestartPlanDetailExample,
} from "./CarUnderuseEventsConstants";
import { CarUnderuseEvent } from "@/models/carUnderuseEvents/underuse";
import { getCarUnderuseEventAPI, editCarUnderuseAnswersAPI } from "@/util/api/carUnderuseEvents";
import { getJSTTime, getShortDatetime } from "@/util/datetime";
import TextField from "@/components/atoms/TextField";
import { Autocomplete } from "@/components/molecules/AutoComplete";
import { Messages } from "@/util/Messages";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";

export const CarUnderuseEventsEditPage = () => {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);

  const [searchParams] = useSearchParams();
  const yearMonth = searchParams.get("yearMonth");

  const [carUnderuseEvent, setCarUnderuseEvent] = React.useState<CarUnderuseEvent>();
  const [editState, setEditState] = React.useState<CarUnderuseEventsEditState>(INITIAL_EDIT_STATE);

  const [errorData, setErrorData] = React.useState<CarUnderuseEventsEditError>({});

  const [showConfirm, setShowConfirm] = React.useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // 一覧画面遷移
  const goToList = React.useCallback(
    () =>
      navigate(`/car_underuse_events`, {
        state: location.state,
      }),
    [location.state, navigate]
  );

  const handleChangeUnderuseReason = React.useCallback(
    (e: React.SyntheticEvent, value: string | null) => {
      setEditState((prev) => ({
        ...prev,
        underuseReason: value ?? getUnderuseReasonLabel(UnderuseReasonCodes.UNDEFINED),
      }));
    },
    []
  );

  const handleChangeRestartPlanDate = React.useCallback(
    (date: string | null) => {
      console.log(date);
      if (!date || isNaN(new Date(date).getTime())) {
        setErrorData((prev) => ({
          ...prev,
          restartPlanDate: true,
        }));
        return;
      }

      let dateStr = getJSTTime(new Date(date), "date");
      setEditState((prev) => ({
        ...prev,
        restartPlanDate: dateStr,
      }));
    },
    []
  );

  const handleChangePlanDescription = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEditState((prev) => ({
        ...prev,
        restartPlanDetail: event.target.value,
      }));
    },
    []
  );

  const [loading, setLoading] = React.useState(false);
  const upSert = React.useCallback(async () => {
    if (loading) {
      return;
    }
    setLoading(true);

    const request = resolveEditRequest(editState);

    editCarUnderuseAnswersAPI(carUnderuseEvent!.evCarId, carUnderuseEvent!.yearMonth, request)
      .then(() => {
        setLoading(false);
        goToList();
      })
      .catch((e: AxiosError) => {
        console.error("===Error occurred while update CarUnderuseEvent===", e);
      })
      .finally(() => setLoading(false));
  }, [editState, carUnderuseEvent, loading, goToList]);

  // Handle submit, check validate and fetch API update
  const onSubmit = React.useCallback(() => {
    // Check validate with each field
    const errorDataTemp: CarUnderuseEventsEditError = {};
    errorDataTemp.underuseReason = (!editState.underuseReason &&
      editState.underuseReason === getUnderuseReasonLabel(UnderuseReasonCodes.UNDEFINED));
    errorDataTemp.restartPlanDate = !editState.restartPlanDate;
    errorDataTemp.planDescription = !editState.restartPlanDetail;

    // If have error then return and show error
    if (Object.values(errorDataTemp).some((error) => error)) {
      setErrorData(errorDataTemp);
      return;
    }

    setErrorData({});
    setShowConfirm(true);
  }, [editState]);

  const getCarUnderuseEvent = React.useCallback(
    () => {
      getCarUnderuseEventAPI(id, yearMonth ?? "")
        .then((res) => {
          const event = res.data;
          setCarUnderuseEvent(event);
        })
        .catch((error) => {
          console.error("Error fetching car underuse event:", error);
          goToList();
        });
    }, [id, yearMonth, goToList]
  );

  // Load CarUnderuseEvent
  React.useEffect(() => {
    getCarUnderuseEvent();
  }, [getCarUnderuseEvent]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>{CarUnderuseEventsConstants.Edit}</Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <Table
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: "0px 4px",
                }}
                className="gx-info-table"
              >
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={CarUnderuseEventsConstants.YearMonth}
                        value={carUnderuseEvent ? formatYearMonth(carUnderuseEvent.yearMonth) : ""}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={CarUnderuseEventsConstants.Company}
                        value={carUnderuseEvent?.companyName || ""}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={CarUnderuseEventsConstants.Office}
                        value={carUnderuseEvent?.officeName || ""}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={CarUnderuseEventsConstants.RadioNumber}
                        value={carUnderuseEvent?.vehicleCode || "-"}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={CarUnderuseEventsConstants.PlateNumber}
                        value={carUnderuseEvent?.plateNumberText || "-"}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={CarUnderuseEventsConstants.EventAt}
                        value={carUnderuseEvent ? getShortDatetime(carUnderuseEvent.eventAt) : ""}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={CarUnderuseEventsConstants.MonthlyRunKm}
                        value={carUnderuseEvent?.monthlyRunKm ?? "-"}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0, pt: 3, border: "none" }}>
                      <Autocomplete
                        options={UNDERUSE_REASONS.map(r => r.label)}
                        label={CarUnderuseEventsConstants.UnderuseReason}
                        value={editState.underuseReason || null}
                        onChange={handleChangeUnderuseReason}
                        error={!!errorData.underuseReason}
                        errorLabel={
                          CarUnderuseEventsConstants.UnderuseReason +
                          Messages.PleaseEnter
                        }
                        sx={{ width: "50%" }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0, pt: 5, border: "none" }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
                        <FormControl required sx={{ width: "50%" }}>
                          <DatePicker
                            label={CarUnderuseEventsConstants.RestartPlanDate}
                            value={editState.restartPlanDate || null}
                            onChange={handleChangeRestartPlanDate}
                            disabled={loading}
                            renderInput={(params) => (
                              <MUITextField
                                {...params}
                                fullWidth
                                variant="outlined"
                                required
                                error={errorData.restartPlanDate}
                                helperText={errorData.restartPlanDate ? "必須項目です" : ""}
                              />
                            )}
                          />
                        </FormControl>
                      </LocalizationProvider>
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        ※稼働再開予定が来月以降で、正確な日付が不明な場合は、再開月の月末の日付をご選択ください
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0, pt: 5, border: "none" }}>
                      <FormControl sx={{ width: "100%" }}>
                        <FormLabel sx={{ mb: 1 }}>
                          {CarUnderuseEventsConstants.RestartPlanDetail}
                        </FormLabel>
                        {editState.underuseReason && editState.underuseReason !== getUnderuseReasonLabel(UnderuseReasonCodes.UNDEFINED) && (
                          <Box>
                            {resolveRestartPlanDetailExample(editState.underuseReason).map((example, index) => (
                              <Box key={index} sx={{ mb: 1 }}>
                                <Typography variant="body2">
                                  {resolveRestartPlanDetailExample(editState.underuseReason).length > 1
                                    ? `記入例${index + 1}`
                                    : '記入例'}
                                </Typography>
                                <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                    {example}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        )}
                        <MUITextField
                          multiline
                          rows={7}
                          value={editState.restartPlanDetail || ""}
                          placeholder={CarUnderuseEventsConstants.DetailPlaceHolder}
                          onChange={handleChangePlanDescription}
                          error={errorData.planDescription}
                          helperText={errorData.planDescription ? "必須項目です" : ""}
                          sx={{ width: "100%" }}
                        />
                      </FormControl>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <ActionButtons
                btnType="Register"
                loading={loading}
                onCancel={goToList}
                onSubmit={onSubmit}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
      <ConfirmDialog
        visible={showConfirm}
        headerTitle={CarUnderuseEventsConstants.Confirm}
        type="Normal"
        textAlign="left"
        onCancel={() => setShowConfirm(false)}
        onOk={() => {
          setShowConfirm(false);
          upSert();
        }}
      >
        <Typography gutterBottom sx={{ whiteSpace: "pre-line" }}>
          {getEditWarnMessage(editState)}
        </Typography>
      </ConfirmDialog>
    </React.Fragment>
  );
};
